import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import styles from "../styles/styles.module.css";
import classnames from "classnames";

const ANIMATION_TIME = 1000;

export const CarouselMode = {
  pagination: "pagination",
  endlessSideScroll: "endlessSideScroll",
};

export const Carousel = ({
  children,
  itemsPerRow,
  carouselMode = CarouselMode.pagination,
  columnWidth = 168,
  ariaDescribedby,
  ariaDescribedbyPagination,
  carouselTestId,
  paginationLabel,
  paginationLabelNoPrevious,
  paginationLabelNoNext,
  paginationTabLabel,
  paginationTabPanelLabel,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const timeoutIdRef = useRef();
  const listElementsRef = useRef([]);

  const getItemsPerRow = () => {
    return carouselMode === CarouselMode.endlessSideScroll
      ? children.length
      : itemsPerRow;
  };

  const maxPage = Math.ceil(children.length / getItemsPerRow()) - 1;

  useEffect(() => clearTimeout(timeoutIdRef.current), []);

  const changePage = useCallback(
    (newPage) => {
      setCurrentPage(newPage);

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }

      showAllLists(listElementsRef.current);
      scrollCarousel(listElementsRef.current, newPage);

      timeoutIdRef.current = setTimeout(() => {
        const focusableElements = getFocusableElements(
          listElementsRef.current[newPage]
        );
        if (focusableElements.length) {
          focusableElements[0].focus();
        }
        manageActiveListVisibility(listElementsRef.current, newPage);
      }, ANIMATION_TIME);
    },
    [maxPage]
  );

  useEffect(() => {
    if (currentPage > maxPage) {
      changePage(maxPage);
    }
  }, [itemsPerRow, changePage, currentPage, maxPage]);

  if (children.length === 0) return null;

  const handleNextClick = () => changePage(currentPage + 1);
  const handlePreviousClick = () => changePage(currentPage - 1);
  const handlePageClick = (index) => () => changePage(index);

  const showItem = (index, page) => {
    const minIndexShown = page * getItemsPerRow();
    const maxIndexShown = (page + 1) * getItemsPerRow();

    return index >= minIndexShown && index < maxIndexShown;
  };

  const getPaginationAriaLabel = (pageIndex, translateFunction) => {
    const lowerProductIndex = getItemsPerRow() * pageIndex + 1;
    const upperProductIndex = Math.min(
      lowerProductIndex + getItemsPerRow() - 1,
      children.length
    );

    return translateFunction(
      lowerProductIndex,
      upperProductIndex,
      children.length
    );
  };

  const buildButtonAriaLabels = ({ lowerProductIndex, upperProductIndex }) => {
    return paginationTabLabel(
      lowerProductIndex,
      upperProductIndex,
      children.length
    );
  };

  const getNextButtonAriaLabel = (disabled) => {
    if (disabled) {
      return paginationLabelNoNext;
    }

    const lowerProductIndex = itemsPerRow * (currentPage + 1) + 1;

    const upperProductIndex = Math.min(
      lowerProductIndex + itemsPerRow - 1,
      children.length
    );

    return buildButtonAriaLabels({ lowerProductIndex, upperProductIndex });
  };

  const getPreviousButtonAriaLabel = (disabled) => {
    if (disabled) {
      return paginationLabelNoPrevious;
    }

    const lowerProductIndex = itemsPerRow * (currentPage - 1) + 1;

    const upperProductIndex = Math.min(
      lowerProductIndex + itemsPerRow - 1,
      children.length
    );

    return buildButtonAriaLabels({ lowerProductIndex, upperProductIndex });
  };

  const renderPaginationButtons = (pageArray) =>
    maxPage > 0 && (
      <nav>
        <ul
          className={styles.pagination}
          aria-label={paginationLabel}
          aria-describedby={ariaDescribedbyPagination}
        >
          {pageArray.map((_, index) => {
            const selected = currentPage === index;

            const classes = `${styles.pageButton} ${
              selected ? styles.active : ""
            }`;
            const ariaLabel = getPaginationAriaLabel(index, paginationTabLabel);

            return (
              <li key={index}>
                <button
                  className={classes}
                  onClick={handlePageClick(index)}
                  aria-current={selected}
                  aria-label={ariaLabel}
                />
              </li>
            );
          })}
        </ul>
      </nav>
    );

  const pageArray = [...Array(maxPage + 1)];
  const disableNext = currentPage === maxPage;
  const disablePrevious = currentPage === 0;

  const shouldHideButtons = carouselMode === CarouselMode.endlessSideScroll;

  const listItemSpacing = 16;

  return (
    <section data-testid={carouselTestId} aria-describedby={ariaDescribedby}>
      <div
        className={classnames({
          [styles.carousel]: true,
          [styles.carouselEndlessSideScroll]:
            carouselMode === CarouselMode.endlessSideScroll,
          [styles.carouselWithPagination]:
            carouselMode === CarouselMode.pagination,
        })}
      >
        {!shouldHideButtons && (
          <button
            disabled={disablePrevious}
            className={styles.previous}
            onClick={handlePreviousClick}
            aria-label={getPreviousButtonAriaLabel(disablePrevious)}
          >
            <span className={`${styles.previousIcon}`} />
          </button>
        )}
        <div
          className={styles.carouselWindow}
          style={{
            width:
              carouselMode === CarouselMode.pagination
                ? `${
                    (columnWidth + listItemSpacing) * itemsPerRow -
                    listItemSpacing
                  }px`
                : null,
          }}
        >
          {pageArray.map((_, pageIndex) => (
            <ul
              key={pageIndex}
              className={styles.list}
              aria-label={getPaginationAriaLabel(
                pageIndex,
                paginationTabPanelLabel
              )}
              aria-hidden={currentPage !== pageIndex}
              ref={(element) => {
                listElementsRef.current[pageIndex] = element;
              }}
            >
              {children.map(
                (item, index) =>
                  showItem(index, pageIndex) && (
                    <li
                      key={index}
                      className={styles.listItem}
                      style={{ width: `${columnWidth}px` }}
                    >
                      {item}
                    </li>
                  )
              )}
            </ul>
          ))}
        </div>
        {!shouldHideButtons && (
          <button
            disabled={disableNext}
            className={styles.next}
            onClick={handleNextClick}
            aria-label={getNextButtonAriaLabel(disableNext)}
          >
            <span className={`${styles.nextIcon}`} />
          </button>
        )}
      </div>
      {renderPaginationButtons(pageArray)}
    </section>
  );
};

const manageActiveListVisibility = (lists, currentPage) => {
  lists.forEach((element, index) => {
    if (element) {
      element.style.visibility = currentPage === index ? "visible" : "hidden";
    }
  });
};

const showAllLists = (lists) => {
  lists.forEach((element) => {
    if (element) {
      element.style.visibility = "visible";
    }
  });
};

const getFocusableElements = (element) => {
  return (
    (element &&
      element.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )) ||
    []
  );
};

const scrollCarousel = (lists, currentPage) => {
  if (lists.length) {
    lists[0].style.marginLeft = `-${100 * currentPage}%`;
  }
};

Carousel.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  /**
   Set the number of items to display per row
   */
  itemsPerRow: PropTypes.number.isRequired,
  /**
   Set the aria-describeby attribute for the carousel
   */
  ariaDescribedby: PropTypes.string,
  /**
   Set the aria-describeby attribute for the pagination buttons
   */
  ariaDescribedbyPagination: PropTypes.string,
  /**
   Set a test id attribute for the carousel
   */
  carouselTestId: PropTypes.string,
  /**
   Override the item column width
   */
  columnWidth: PropTypes.number,
  /**
   Carousel display mode, currently "endlessSideScroll" or "pagination", defaults to pagination
   */
  carouselMode: PropTypes.oneOf(Object.values(CarouselMode)),
  /**
   Static translation for the pagination buttons
   */
  paginationLabel: PropTypes.string,
  /**
   Static translation for the previous button when previous is disabled
   */
  paginationLabelNoPrevious: PropTypes.string,
  /**
   Static translation for the next button when next is disabled
   */
  paginationLabelNoNext: PropTypes.string,
  /**
   Function that passes the lower count, upper count and total count, expects the static translation string returned
   String is used on the next/previous buttons to describe the next page
   */
  paginationTabLabel: PropTypes.func,
  /**
   Function that passes the lower count, upper count and total count, expects the static translation string returned
   String is used to describe each 'page' of items
   */
  paginationTabPanelLabel: PropTypes.func,
};
